import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../state/language/LanguageProvider';
import { crmService, Interaction } from '../../services/crmCRUD';

interface DashboardStats {
  companies: number;
  contacts: number;
  interactions: number;
  recentInteractions: Interaction[];
}

export const CRMDashboard: React.FC = () => {
  const { t } = useLanguage();
  const [stats, setStats] = useState<DashboardStats>({
    companies: 0,
    contacts: 0,
    interactions: 0,
    recentInteractions: []
  });
  const [loading, setLoading] = useState(true);
  const [hoveredInteraction, setHoveredInteraction] = useState<any>(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const handleMouseEnter = (interaction: any, event: React.MouseEvent) => {
    setHoveredInteraction(interaction);
    setHoverPosition({ x: event.clientX, y: event.clientY });
  };

  const handleMouseLeave = () => {
    setHoveredInteraction(null);
  };

  const getInteractionDescription = (interaction: any) => {
    const contactName = interaction.contact_name || 'Unknown Contact';
    const companyName = interaction.company_name;
    const subject = interaction.subject;
    const interactionType = interaction.interaction_type?.toLowerCase() || 'meeting';

    switch (interactionType) {
      case 'introduction':
        return (
          <>
            <span className="font-medium text-gray-900">{contactName}</span>
            {' '}introduced{' '}
            {subject && (
              <>
                <span className="font-medium text-gray-900">{subject}</span>
                {' '}to{' '}
              </>
            )}
            {companyName && (
              <span className="font-medium text-gray-900">{companyName}</span>
            )}
          </>
        );
      
      case 'meeting':
      case 'call':
      case 'email':
      default:
        return (
          <>
            Meeting with{' '}
            <span className="font-medium text-gray-900">{contactName}</span>
            {companyName && (
              <>
                {' '}(<span className="font-medium text-gray-900">{companyName}</span>)
              </>
            )}
            {subject && (
              <>
                {' '}about{' '}
                <span className="font-medium text-gray-900">{subject}</span>
              </>
            )}
          </>
        );
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load basic stats using the same approach as individual pages
      const [companiesResp, contactsResp, interactionsResp, recentInteractions] = await Promise.all([
        crmService.getCompanies({ limit: 99999 }), // Get all companies to count them
        crmService.getContacts({ limit: 99999 }), // Get all contacts to count them
        crmService.getInteractions({ limit: 99999 }), // Get all interactions to count them
        crmService.getInteractions({ limit: 5 }) // Get only 5 recent interactions
      ]);

      setStats({
        companies: companiesResp.total || companiesResp.data?.length || 0,
        contacts: contactsResp.total || contactsResp.data?.length || 0,
        interactions: interactionsResp.total || interactionsResp.data?.length || 0,
        recentInteractions: recentInteractions.data || []
      });
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-primary-bg)] mx-auto"></div>
          <p className="mt-2 text-[var(--color-secondary-text)]">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <>

          {/* Stats Cards - Directly below header */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Companies Card */}
            <div className="bg-white overflow-hidden shadow rounded-lg border border-[var(--color-border)]">
              <div className="p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-[var(--color-primary-bg)] rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-[var(--color-secondary-text)] truncate">
                        Total Companies
                      </dt>
                      <dd className="text-lg font-medium text-[var(--color-surface-text)]">
                        {stats.companies}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            {/* Contacts Card */}
            <div className="bg-white overflow-hidden shadow rounded-lg border border-[var(--color-border)]">
              <div className="p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-[var(--color-success)] rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-[var(--color-secondary-text)] truncate">
                        Total Contacts
                      </dt>
                      <dd className="text-lg font-medium text-[var(--color-surface-text)]">
                        {stats.contacts}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            {/* Interactions Card */}
            <div className="bg-white overflow-hidden shadow rounded-lg border border-[var(--color-border)]">
              <div className="p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-[var(--color-accent-bg)] rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-[var(--color-secondary-text)] truncate">
                        Total Interactions
                      </dt>
                      <dd className="text-lg font-medium text-[var(--color-surface-text)]">
                        {stats.interactions}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Interactions - Below stats */}
          <div className="mt-6">
            <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-sm border border-[var(--color-border)] px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-[var(--color-surface-text)] mb-4">
                Recent Interactions
              </h3>
              {stats.recentInteractions.length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  No recent interactions
                </p>
              ) : (
                <div className="flow-root">
                  <ul className="-mb-8">
                    {stats.recentInteractions.map((interaction: any, index: number) => (
                      <li key={interaction.id}>
                        <div className="relative pb-8">
                          {index !== stats.recentInteractions.length - 1 && (
                            <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                          )}
                          <div 
                            className="relative flex space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                            onMouseEnter={(e) => handleMouseEnter(interaction, e)}
                            onMouseLeave={handleMouseLeave}
                          >
                            <div>
                              <span className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center ring-8 ring-white">
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                                </svg>
                              </span>
                            </div>
                            <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                              <div>
                                <p className="text-sm text-gray-500">
                                  {getInteractionDescription(interaction)}
                                </p>
                              </div>
                              <div className="text-right text-sm whitespace-nowrap text-gray-500">
                                {new Date(interaction.created_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Hover Tooltip */}
          {hoveredInteraction && (
            <div 
              className="fixed z-50 bg-gray-900 text-white p-4 rounded-lg shadow-lg pointer-events-none"
              style={{
                left: `${hoverPosition.x + 10}px`,
                top: `${hoverPosition.y - 10}px`,
                transform: 'translateY(-100%)',
                minWidth: '300px',
                maxWidth: '500px',
                width: 'auto'
              }}
            >
              <div className="text-sm font-semibold mb-2 border-b border-gray-700 pb-2">
                {hoveredInteraction.interaction_type?.toUpperCase() || 'INTERACTION'}
              </div>
              
              <div className="text-sm">
                {/* Show only discussion details */}
                {hoveredInteraction.description ? (
                  <div className="text-gray-100 bg-gray-800 p-3 rounded max-h-60 overflow-y-auto whitespace-pre-wrap break-words">
                    {hoveredInteraction.description}
                  </div>
                ) : (
                  <div className="text-gray-400 italic">No discussion details available</div>
                )}
              </div>
            </div>
          )}
    </>
  );
};
